import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  describePortalEntryNumber,
  exchangePortalGuidForSession,
  fetchPortalSubscriptions,
  requestPortalCancel,
  requestPortalDownsell,
  requestPortalEntryByNumber,
  requestPortalResume,
  resolvePortalEntryCode,
  type PortalAccess,
  type PortalCancelResult,
  type PortalDownsellOutcome,
  type PortalResumeOutcome,
  type PortalSubscriptionView,
} from '@/api/portal';
import { formatPublicQuizDate } from '@/utils/publicQuizStrings';
import type { PublicStripePrice } from '@/utils/formatStripePrice';
import type { DownsellOfferAppearance } from '../semanticTextRoles';

/**
 * WEB-1152 (П9) атом 3 — единый action-контур портала отмены с удержанием.
 *
 * По образцу paywallCheckoutContext (WEB-703, единый money-путь пейволла):
 * провайдер ОДИН раз захватывает вход из URL публичного портал-пейволла —
 * `?guid=` (прямая manage_link) ЛИБО `?code=` (email-ссылка; одноразовый
 * portal-токен резолвится через GET /public/handoff/portal/resolve →
 * {guid, projectId}, резолвнутые значения авторитетнее пропа страницы) —
 * один раз грузит подписки и раздаёт блокам subscription_list /
 * cancel_button / downsell_offer данные + действия. Блоки не дёргают API
 * сами — иначе три блока на одной странице сделали бы три одинаковых
 * запроса и разъехались бы в состоянии после отмены.
 *
 * `null` из usePortalActions = блок живёт вне портала (канвас редактора,
 * обычный пейволл без guid-провайдера) → блок рисует инертное превью.
 * Обычный пейволл без ?guid= и ?code= остаётся инертным: провайдер ничего
 * не грузит, портальные блоки показывают «ссылка недействительна».
 */
/** Типы портальных блоков, которые может нести страница пейволла. */
export type PortalBlockType =
  | 'subscription_list'
  | 'cancel_button'
  // WEB-1395: возобновление подписки (снятие запланированной отмены).
  | 'resume_button'
  | 'downsell_offer';

/**
 * WEB-1183: разрешённая конфигурация активного блока `downsell_offer`
 * страницы — то, чем сам блок наполняет свою карточку (см.
 * DownsellOfferBlock.tsx). Шаг подтверждения отмены (CancelSubscriptionButton)
 * читает этот конфиг из контекста, а не из своих пропов: владелец страницы
 * настраивает блоки независимо, и cancel_button не получает чужих пропов
 * напрямую (см. PublicBlockRendererExtended — каждый блок получает только
 * СВОИ поля).
 */
export interface DownsellOfferConfig {
  title?: string;
  text?: string;
  ctaLabel?: string;
  successText?: string;
  targetPriceId: string;
  /** Показывать оффер только владельцам этих исходных цен; пусто = всем. */
  sourcePriceIds?: string[];
  stripePrices?: PublicStripePrice[];
  primaryColor?: string;
  /**
   * WEB-1692 юнит 5 — ОФОРМЛЕНИЕ, ЗАДАННОЕ АВТОРОМ, ЕДЕТ ВМЕСТЕ С КОНФИГОМ.
   *
   * Карточка оффера рисуется в ДВУХ местах: самим блоком (DownsellOfferBlock)
   * и шагом подтверждения отмены (CancelSubscriptionButton → CancelDownsellCard,
   * WEB-1183), у которого своя разметка и свои пропы. Без этого поля настройка
   * доехала бы до одной двери из двух: автор красит оффер, видит его на
   * канвасе, а покупатель на шаге отмены получает прежний вид.
   */
  appearance?: DownsellOfferAppearance;
}

/**
 * Подписка подходит под условие оффера: живая, без запланированной отмены,
 * ещё не на целевой цене, и (если задано условие) её текущая цена — из
 * списка исходных. Общая проверка для DownsellOfferBlock (карточка на
 * странице) и CancelSubscriptionButton (та же карточка на шаге
 * подтверждения отмены, WEB-1183) — одно условие, не расходится.
 */
export function isDownsellEligible(
  sub: PortalSubscriptionView,
  offer: DownsellOfferConfig,
): boolean {
  return (
    sub.status !== 'canceled' &&
    !sub.cancelAtPeriodEnd &&
    sub.priceId !== offer.targetPriceId &&
    (!offer.sourcePriceIds?.length ||
      (sub.priceId != null && offer.sourcePriceIds.includes(sub.priceId)))
  );
}

export interface PortalActionsValue {
  projectId: string;
  /**
   * WEB-1192 (п.3): есть ли у страницы признанный вход — короткоживущая
   * сессия (sessionStorage) либо легаси-guid текущей загрузки. Сам креденшел
   * блокам НЕ выдаётся: он живёт только внутри провайдера и в API-вызовы
   * уходит из него же (guid из адресной строки больше не существует).
   */
  hasAccess: boolean;
  /**
   * WEB-1192 (п.3): true = сессия портала протухла (бэкенд перестал её
   * признавать). Блоки показывают «сессия закончилась — запросите новую
   * ссылку» и форму письма, а не общий «ссылка недействительна».
   */
  sessionExpired: boolean;
  /**
   * Ревизия WEB-1152: true = в URL был ?code=, но резолв его отверг
   * (просрочен/уже использован). Блоки показывают честный текст про
   * одноразовость ссылки, а не общий «ссылка недействительна».
   */
  codeInvalid: boolean;
  /**
   * WEB-1416 п.1: true = в URL был ?guid= (прямая manage_link — теперь несёт
   * короткоживущий подписанный токен, TTL 15 мин), но обмен на сессию
   * (POST /public/portal/session) не удался — протух/испорчен/чужого
   * формата (бэкенд не различает причины наружу, anti-enum). Блоки
   * показывают честный текст «ссылка устарела — запросите новую», а не
   * общий «ссылка недействительна» и не бесконечный лоадер.
   */
  guidInvalid: boolean;
  loading: boolean;
  /** true = запрос подписок упал (сеть/404) — показать ошибку, не белый экран. */
  error: boolean;
  subscriptions: PortalSubscriptionView[];
  /**
   * Ревизия 5 (дефекты 2 и 3): заявка ЭКЗЕМПЛЯРА видимого портального блока на
   * право напечатать приглашение войти по почте. Зовётся блоком ПРЯМО В
   * РЕНДЕРЕ (через usePortalBlockPresence) и отвечает синхронно: true = печатать
   * этому экземпляру.
   *
   * Почему в рендере, а не в эффекте (дефект 2): реестр, наполняемый отложенным
   * эффектом, на ПЕРВОМ кадре пуст — хозяина нет, и покупатель видит полностью
   * пустой экран, пока React не прогонит эффекты и не перерисует.
   *
   * Почему по экземпляру, а не по типу (дефект 3): два блока одного типа на
   * экране давали истинное условие обоим, и приглашение печаталось дважды.
   *
   * Правило выбора: печатает ПЕРВЫЙ зарегистрировавшийся видимый экземпляр,
   * то есть верхний портальный блок экрана (порядок регистрации = порядок
   * рендера). Прежний жёсткий приоритет по типам убран: он требовал знать весь
   * набор блоков заранее, а один кадр рендерится по одному блоку за раз.
   */
  claimInviteSlot: (instanceKey: string) => boolean;
  /** Снятие заявки при размонтировании блока (смена экрана визарда). */
  releaseInviteSlot: (instanceKey: string) => void;
  /**
   * Дефект 4: состояние формы входа живёт в ПРОВАЙДЕРЕ, а не в блоке-хозяине.
   * Состав видимых блоков меняется → меняется хозяин → форма внутри блока
   * размонтировалась бы: набранная почта терялась, а человек отправлял письмо
   * повторно — и первая ссылка (одноразовая) сгорала.
   */
  entryEmail: string;
  setEntryEmail: (value: string) => void;
  entryLinkSent: boolean;
  markEntryLinkSent: () => void;
  /**
   * WEB-3413: страница открыта по ссылке «Управлять подпиской» из письма
   * после оплаты (`?entry=<номер>`) и номер признан — МАСКА почты покупки
   * (`s***@gmail.com`). null — номера нет, он неизвестен/просрочен или
   * покупатель выбрал другую почту: форма обычная. Сам номер блокам НЕ
   * выдаётся — он живёт только в провайдере.
   */
  entryMaskedEmail: string | null;
  /** WEB-3413: попросить письмо входа на почту покупки по номеру. */
  requestEntryByNumber: (language?: string) => Promise<boolean>;
  /** WEB-3413: «другая почта» — забыть номер, показать обычную форму. */
  forgetEntryNumber: () => void;
  /** Плановая отмена; после успеха локальное зеркало подписки обновляется. */
  cancel: (subscriptionId: string) => Promise<PortalCancelResult | null>;
  /**
   * WEB-1395: возобновление (снятие запланированной отмены). После успеха
   * локальное зеркало обновляется: подписка снова активна, cancel_button
   * снова рисует живую кнопку отмены, downsell — прежняя логика.
   */
  resume: (subscriptionId: string) => Promise<PortalResumeOutcome>;
  /** Принятие скидки (swap на targetPriceId со следующего периода). */
  downsell: (
    subscriptionId: string,
    targetPriceId: string,
  ) => Promise<PortalDownsellOutcome>;
  /**
   * WEB-1183: конфиг активного блока `downsell_offer` этой страницы портала
   * (если он на ней есть) — шаг подтверждения отмены переиспользует его,
   * чтобы предложить скидку ровно в момент намерения уйти («уходишь? вот
   * цена ниже»), а не только рядом с кнопкой отмены, где оффер бесполезен
   * до отмены и исчезает сразу после неё. null = блока нет на странице
   * либо он ещё не смонтировался.
   */
  downsellOffer: DownsellOfferConfig | null;
  /** Блок downsell_offer заявляет провайдеру свою (перезаписываемую) конфигурацию. */
  registerDownsellOffer: (
    instanceKey: string,
    config: DownsellOfferConfig,
  ) => void;
  /** Снятие заявки при размонтировании/скрытии блока. */
  unregisterDownsellOffer: (instanceKey: string) => void;
}

const PortalActionsContext = createContext<PortalActionsValue | null>(null);

export function usePortalActions(): PortalActionsValue | null {
  return useContext(PortalActionsContext);
}

/**
 * Портальный блок сообщает провайдеру, что он СЕЙЧАС на экране, и узнаёт,
 * ему ли печатать приглашение войти по почте. Возвращает true ровно одному
 * видимому экземпляру на экран.
 *
 * Хук зовётся БЕЗУСЛОВНО, до любых ранних return'ов блока (правила хуков) —
 * поэтому заявка означает «блок примонтирован на текущем экране», а не «блок
 * что-то нарисовал». Вне провайдера (канвас редактора) — всегда false.
 *
 * Ревизия 5 (дефект 2): заявка подаётся ПРЯМО В РЕНДЕРЕ, а не в эффекте —
 * иначе первый кадр экрана пуст. Ключ экземпляра берётся из useId(): он
 * стабилен для позиции в дереве, поэтому повторный прогон рендера (React
 * StrictMode) не плодит фантомных заявок.
 */
export function usePortalBlockPresence(): boolean {
  const portal = useContext(PortalActionsContext);
  const instanceKey = useId();
  const claim = portal?.claimInviteSlot;
  const release = portal?.releaseInviteSlot;
  const isInviteHost = claim ? claim(instanceKey) : false;
  useEffect(() => {
    if (!claim) return;
    // Повторная заявка идемпотентна: нужна на случай, если эффект
    // переподписался (снятие заявки в cleanup идёт ПОСЛЕ нового рендера).
    claim(instanceKey);
    return () => {
      release?.(instanceKey);
    };
  }, [instanceKey, claim, release]);
  return isInviteHost;
}

/**
 * WEB-1183: блок `downsell_offer` заявляет провайдеру свою конфигурацию
 * (или снимает заявку, если сейчас нечего предлагать: нет portal-провайдера
 * либо не задан targetPriceId) — CancelSubscriptionButton читает её как
 * `portal.downsellOffer`, чтобы показать тот же оффер на шаге подтверждения
 * отмены. Регистрация — в эффекте (не в рендере, в отличие от
 * usePortalBlockPresence): здесь не нужен первый кадр день-в-день, а
 * первый рендер CancelSubscriptionButton происходит задолго до клика по
 * «Отменить», так что эффект успевает отработать. `configKey`
 * (сериализация) — стабильная зависимость эффекта: `config` — новый
 * объект на каждый рендер блока, а перерегистрация должна происходить
 * только при изменении СОДЕРЖИМОГО, не ссылки.
 */
export function usePortalDownsellOfferRegistration(
  config: DownsellOfferConfig | null,
): void {
  const portal = useContext(PortalActionsContext);
  const instanceKey = useId();
  const register = portal?.registerDownsellOffer;
  const unregister = portal?.unregisterDownsellOffer;
  const configKey = config ? JSON.stringify(config) : null;
  // Ревизия десятки 7: перерегистрация при смене configKey БЕЗ cleanup —
  // register() заменяет запись по ключу НА МЕСТЕ (см. registerDownsellOffer),
  // сохраняя позицию в реестре. Прежний вариант (cleanup+register на каждую
  // смену deps) сначала выбрасывал запись splice'ом, потом добавлял в конец —
  // правка настроек блока №1 молча передавала «победу» блоку №2.
  useEffect(() => {
    if (!register || !unregister) return;
    if (configKey) {
      register(instanceKey, config as DownsellOfferConfig);
    } else {
      unregister(instanceKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceKey, register, unregister, configKey]);
  // Снятие заявки — только при размонтировании блока.
  useEffect(() => {
    if (!unregister) return;
    return () => {
      unregister(instanceKey);
    };
  }, [instanceKey, unregister]);
}

/** Достаёт guid из строки поиска URL; пустой/пробельный = null. */
export function readPortalGuid(search: string): string | null {
  const raw = new URLSearchParams(search).get('guid');
  const trimmed = raw?.trim();
  return trimmed ? trimmed : null;
}

/** Достаёт portal-код (?code=, email-ссылка) из строки поиска; пустой = null. */
export function readPortalCode(search: string): string | null {
  const raw = new URLSearchParams(search).get('code');
  const trimmed = raw?.trim();
  return trimmed ? trimmed : null;
}

/**
 * WEB-3413: номер ссылки «Управлять подпиской» из письма после оплаты
 * (`?entry=`); пустой/пробельный = null. Номер — не вход: по нему сервер
 * отдаёт только маску почты и шлёт письмо входа на почту покупки.
 */
export function readPortalEntryNumber(search: string): string | null {
  const raw = new URLSearchParams(search).get('entry');
  const trimmed = raw?.trim();
  return trimmed ? trimmed : null;
}

/**
 * WEB-3413: убрать `?entry=` из адреса (остальные параметры не трогаем) —
 * номер не должен оставаться в истории браузера, в закладке и в переходах
 * со страницы дальше.
 */
function scrubEntryNumberFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('entry')) return;
    url.searchParams.delete('entry');
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // best-effort: неудача переписи URL не должна ломать страницу.
  }
}

/**
 * WEB-1192 (п.3): ключ sessionStorage, под которым живёт креденшел портала.
 * sessionStorage, НЕ localStorage: креденшел умирает вместе с вкладкой и не
 * достаётся следующему человеку за общим компьютером; F5 при этом живёт.
 */
export const PORTAL_SESSION_STORAGE_KEY = 'qbPortalSession';

/**
 * Что лежит в sessionStorage: сессионный токен (основной путь) либо
 * легаси-guid (обмен был недоступен — старый бэкенд/сеть). projectId — тот,
 * для которого креденшел выпущен (авторитетнее пропа страницы, как и
 * projectId из резолва кода).
 */
interface StoredPortalCredential {
  projectId: string;
  sessionToken?: string;
  guid?: string;
}

/**
 * FIX-P12-1 (ревизия пачки 12, находка 2): sessionStorage — общий на вкладку
 * ключ (PORTAL_SESSION_STORAGE_KEY без неймспейса), а не per-проект. Портал
 * проекта A, затем портал-пейволл проекта B в ТОЙ ЖЕ вкладке — до этой
 * проверки запись A читалась и считалась авторитетнее пропа страницы
 * безусловно, и подписки A рисовались на странице B (не тот тенант: данные
 * того же покупателя, но чужого проекта). Правим сравнением с projectId
 * СТРАНИЦЫ здесь, а не неймспейсингом ключа (`qbPortalSession:<projectId>`):
 * неймспейс потребовал бы миграции формата ключа и не более безопасен —
 * несовпадающий credential всё равно нужно единообразно отвергать, а не
 * доверять любому чужому projectId в значении.
 */
function readStoredPortalCredential(
  pageProjectId: string,
): StoredPortalCredential | null {
  try {
    const raw = window.sessionStorage.getItem(PORTAL_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPortalCredential | null;
    if (!parsed || typeof parsed.projectId !== 'string' || !parsed.projectId) {
      return null;
    }
    if (parsed.projectId !== pageProjectId) return null;
    const sessionToken =
      typeof parsed.sessionToken === 'string' && parsed.sessionToken
        ? parsed.sessionToken
        : undefined;
    const guid =
      typeof parsed.guid === 'string' && parsed.guid ? parsed.guid : undefined;
    if (sessionToken) return { projectId: parsed.projectId, sessionToken };
    if (guid) return { projectId: parsed.projectId, guid };
    return null;
  } catch {
    // Приватный режим/запрет storage — вход просто не переживёт F5.
    return null;
  }
}

function storePortalCredential(value: StoredPortalCredential): void {
  try {
    window.sessionStorage.setItem(
      PORTAL_SESSION_STORAGE_KEY,
      JSON.stringify(value),
    );
  } catch {
    // best-effort: недоступный storage не должен ломать вход.
  }
}

function clearStoredPortalCredential(): void {
  try {
    window.sessionStorage.removeItem(PORTAL_SESSION_STORAGE_KEY);
  } catch {
    // best-effort
  }
}

/**
 * WEB-1192 (п.3): убрать входные ключи из адресной строки — И одноразовый
 * ?code=, И вечный ?guid= (раньше сюда наоборот ЗАПИСЫВАЛИ guid ради F5 —
 * теперь F5 живёт за счёт sessionStorage). Остальные параметры сохраняются.
 */
function scrubEntryParamsFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('code');
    // FIX-P12-1 (находка 4, на будущее): `?guid=` — общий query-параметр с
    // app-guid (utils/appGuid.ts) для страниц, открытых из приложения
    // (`origin=app`). Тот резолв персистит guid в sessionStorage СВОИМ
    // эффектом чуть позже — если портальный блок выдернет guid из URL
    // раньше, визард пейволла на следующем шаге резолвит уже обычный
    // веб-visitor-id, и app-guid для этой загрузки теряется безвозвратно.
    if (url.searchParams.get('origin') !== 'app') {
      url.searchParams.delete('guid');
    }
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // best-effort: неудача переписи URL не должна ломать вход.
  }
}

/** Внутренний креденшел провайдера: сессия (основная) или легаси-guid. */
type PortalCredential =
  { kind: 'session'; token: string } | { kind: 'guid'; guid: string };

function credentialToAccess(credential: PortalCredential): PortalAccess {
  return credential.kind === 'session'
    ? { sessionToken: credential.token }
    : credential.guid;
}

export interface PublicPortalProviderProps {
  projectId: string;
  /**
   * Ревизия 2 (дефекты 5 и 7): какие портальные блоки реально есть на
   * странице. Пусто = портальных блоков нет → ручку подписок НЕ зовём
   * (провайдер висит на каждом публичном пейволле, и обычная воронка,
   * открытая с ?guid=, делала лишний запрос). undefined = набор неизвестен
   * (точечный рендер/тесты) — ведём себя как раньше.
   */
  portalBlockTypes?: PortalBlockType[];
  children: React.ReactNode;
}

export function PublicPortalProvider({
  projectId,
  portalBlockTypes,
  children,
}: PublicPortalProviderProps) {
  // Сигнатура-строка вместо ссылки на массив: страница пересобирает список
  // блоков на каждый рендер (там нельзя useMemo — место после ранних
  // return'ов), и по ссылке мемоизация бы не работала.
  const blockTypesKey =
    portalBlockTypes === undefined
      ? undefined
      : [...portalBlockTypes].sort().join(',');
  // Портал активен, когда набор блоков неизвестен (совместимость: точечный
  // рендер/тесты) либо в нём есть хоть один портальный блок.
  const portalActive = blockTypesKey === undefined || blockTypesKey.length > 0;

  // Ревизия 5 (дефекты 2 и 3): реестр ВИДИМЫХ ЭКЗЕМПЛЯРОВ портальных блоков в
  // порядке заявок. Ref, а не state: заявка подаётся прямо в рендере блока
  // (иначе первый кадр экрана пуст — регресс партии), а setState во время
  // рендера чужого компонента делать нельзя.
  const inviteRegistryRef = useRef<string[]>([]);
  // Версия нужна только чтобы разбудить оставшиеся блоки, когда хозяин ушёл с
  // экрана: они читают контекст и пересчитают заявку.
  const [inviteRegistryVersion, setInviteRegistryVersion] = useState(0);
  const claimInviteSlot = useCallback(
    (instanceKey: string): boolean => {
      if (!portalActive) return false;
      const registry = inviteRegistryRef.current;
      if (!registry.includes(instanceKey)) registry.push(instanceKey);
      // Печатает первый заявившийся = верхний портальный блок экрана.
      return registry[0] === instanceKey;
    },
    [portalActive],
  );
  const releaseInviteSlot = useCallback((instanceKey: string) => {
    const registry = inviteRegistryRef.current;
    const index = registry.indexOf(instanceKey);
    if (index === -1) return;
    registry.splice(index, 1);
    // Ушёл хозяин — приглашение должен подхватить следующий видимый блок.
    if (index === 0) setInviteRegistryVersion((v) => v + 1);
  }, []);

  // WEB-1183: реестр КОНФИГОВ видимых экземпляров downsell_offer, тем же
  // приёмом, что и inviteRegistry выше (ref, версия-будильник в эффекте —
  // регистрация зовётся из useEffect самого блока, не прямо в рендере
  // чужого компонента). На практике страница несёт максимум один
  // downsell_offer — печатается конфиг первого зарегистрировавшегося.
  const downsellRegistryRef = useRef<
    Array<{ key: string; config: DownsellOfferConfig }>
  >([]);
  const [downsellRegistryVersion, setDownsellRegistryVersion] = useState(0);
  const registerDownsellOffer = useCallback(
    (instanceKey: string, config: DownsellOfferConfig) => {
      const registry = downsellRegistryRef.current;
      const index = registry.findIndex((entry) => entry.key === instanceKey);
      if (index === -1) {
        registry.push({ key: instanceKey, config });
      } else {
        registry[index] = { key: instanceKey, config };
      }
      setDownsellRegistryVersion((v) => v + 1);
    },
    [],
  );
  const unregisterDownsellOffer = useCallback((instanceKey: string) => {
    const registry = downsellRegistryRef.current;
    const index = registry.findIndex((entry) => entry.key === instanceKey);
    if (index === -1) return;
    registry.splice(index, 1);
    setDownsellRegistryVersion((v) => v + 1);
  }, []);
  const downsellOffer = downsellRegistryRef.current[0]?.config ?? null;

  // Дефект 4: состояние формы входа — в провайдере, чтобы пережить смену
  // блока-хозяина (иначе набранная почта терялась, а повторная отправка
  // сжигала первую одноразовую ссылку).
  const [entryEmail, setEntryEmail] = useState('');
  const [entryLinkSent, setEntryLinkSent] = useState(false);
  const markEntryLinkSent = useCallback(() => setEntryLinkSent(true), []);

  // Вход читается один раз на маунт: manage_link / email-ссылка открываются
  // новой загрузкой страницы, менять guid/code на живой странице некому.
  // Дефект 7: без портальных блоков вход из URL не читаем вовсе — ни guid,
  // ни code: обычная воронка с ?guid= не должна дёргать портальные ручки.
  const initialGuid = useMemo(
    () => (portalActive ? readPortalGuid(window.location.search) : null),
    [portalActive],
  );
  // ?code= учитываем ТОЛЬКО без ?guid= — прямая manage_link главнее.
  const code = useMemo(
    () =>
      portalActive && !initialGuid
        ? readPortalCode(window.location.search)
        : null,
    [portalActive, initialGuid],
  );
  // WEB-1192 (п.3): восстановление входа после перезагрузки — из
  // sessionStorage (адресная строка теперь чиста). Читается только когда в
  // URL нет ни guid, ни code: живой входной параметр всегда авторитетнее.
  // FIX-P12-1 (находка 2): запись принимается, только если её projectId
  // совпадает с projectId ЭТОЙ страницы — см. докблок readStoredPortalCredential.
  const stored = useMemo(
    () =>
      portalActive && !initialGuid && !code
        ? readStoredPortalCredential(projectId)
        : null,
    [portalActive, initialGuid, code, projectId],
  );
  // Единый креденшел провайдера. WEB-1416 п.1: ?guid= из URL БОЛЬШЕ НЕ несёт
  // identity-guid — это короткоживущий подписанный токен manage_link, годный
  // только на вход в обмен (POST /public/portal/session). Optimistic
  // bare-значение из URL как креденшел ушло вместе с этим — до этой правки
  // сам голый guid был вечным ключом (см. докблок PortalLinkService),
  // теперь он и технически бесполезен для API (endpoint'ы ждут identity-guid
  // или сессию, а тут токен). Тот же паттерн, что у ?code=: credential=null,
  // пока обмен в полёте — эффект ниже докладывает его после успеха.
  // sessionStorage-сессия (в т.ч. легаси-guid прежних визитов) — используется
  // сразу.
  const [credential, setCredential] = useState<PortalCredential | null>(() => {
    if (initialGuid) return null;
    if (stored?.sessionToken) {
      return { kind: 'session', token: stored.sessionToken };
    }
    // WEB-1573: запись с голым guid от прежних визитов креденшелом больше
    // не считается — с закрытием окна совместимости она гарантирует 404 на
    // каждом запросе. Пусто = страница честно приглашает войти по почте.
    return null;
  });
  // projectId из резолва кода/sessionStorage авторитетнее пропа страницы
  // (креденшел знает свой проект); для прямого ?guid= остаётся projectId
  // пейволла.
  const [resolvedProjectId, setResolvedProjectId] = useState<string | null>(
    stored?.projectId ?? null,
  );
  const effectiveProjectId = resolvedProjectId ?? projectId;
  const [subscriptions, setSubscriptions] = useState<PortalSubscriptionView[]>(
    [],
  );
  // WEB-3413: номер ссылки из письма читается один раз и только без живого
  // входа (?guid=, ?code=, сессия вкладки) — живой вход главнее. Из адреса
  // номер убирается в эффекте раскладки ниже, до первого кадра.
  const [entryNumber, setEntryNumber] = useState<string | null>(() =>
    portalActive && !initialGuid && !code && !stored?.sessionToken
      ? readPortalEntryNumber(window.location.search)
      : null,
  );
  const [entryMaskedEmail, setEntryMaskedEmail] = useState<string | null>(
    null,
  );
  // Пока маска в пути — загрузка, а не форма почты (иначе мигнёт поле).
  const [entryPending, setEntryPending] = useState<boolean>(
    entryNumber !== null,
  );
  const [loading, setLoading] = useState<boolean>(
    Boolean(initialGuid || code || stored || entryNumber),
  );
  const [error, setError] = useState(false);
  const [codeInvalid, setCodeInvalid] = useState(false);
  const [guidInvalid, setGuidInvalid] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // WEB-1152 (примирение атомов): email-вход — резолв одноразового
  // portal-кода. WEB-1192 (п.3): резолв теперь отдаёт токен короткоживущей
  // сессии; URL чистится от code И guid (вечный ключ в адрес больше не
  // пишется — F5 живёт за счёт sessionStorage). Ошибка резолва
  // (просрочен/использован) → креденшела нет, loading=false → блоки
  // показывают человекочитаемое «ссылка недействительна», не белый экран.
  useEffect(() => {
    if (!code) return;
    let alive = true;
    void resolvePortalEntryCode(code).then((resolved) => {
      if (!alive) return;
      if (!resolved) {
        setCodeInvalid(true);
        setLoading(false);
        return;
      }
      scrubEntryParamsFromUrl();
      if (resolved.sessionToken) {
        storePortalCredential({
          projectId: resolved.projectId,
          sessionToken: resolved.sessionToken,
        });
        setCredential({ kind: 'session', token: resolved.sessionToken });
      } else {
        // WEB-1573: сессии нет — и запасного входа больше НЕТ. Раньше здесь
        // доживали на голом guid (старый бэкенд окна выката либо сознательный
        // откат сервера, когда минт сессии упал уже ПОСЛЕ погашения кода —
        // public-handoff.controller.ts). Голый идентификатор покупателя
        // решением владельца 25.08 перестал быть пропуском: каждый запрос с
        // ним получит 404, а код письма уже сожжён. Изображать вход, который
        // немедленно упадёт в «ошибка загрузки, попробуйте позже», нечестно и
        // бесполезно — повторять покупателю нечего.
        //
        // Говорим ровно то, что правда: ссылка из письма одноразовая,
        // запросите новую (тот же текст и та же форма, что у просроченного
        // кода). Мёртвый guid в sessionStorage НЕ кладём — иначе F5
        // воскрешал бы несуществующий вход.
        setCodeInvalid(true);
        setLoading(false);
        return;
      }
      setResolvedProjectId(resolved.projectId);
    });
    return () => {
      alive = false;
    };
  }, [code]);

  // WEB-1416 п.1: прямой вход по ?guid= (manage_link) несёт короткоживущий
  // подписанный ТОКЕН (TTL 15 мин), не identity-guid — POST
  // /public/portal/session (createSession) отклоняет голый guid 404. Значение
  // из URL поэтому используется ТОЛЬКО как вход в обмен на сессию — тот же
  // pending-паттерн, что у ?code= выше (loading=true, пока обмен в полёте;
  // credential не выставлен раньше времени). Успех → сессия в sessionStorage,
  // адрес чистится (как раньше, WEB-1192 п.3). Отказ (протухший >15 мин или
  // битый токен — бэкенд не различает причины наружу, anti-enum) →
  // guidInvalid=true, «ссылка устарела»; БЕЗ фолбэка на bare-значение как
  // легаси-guid-креденшел — оно технически бесполезно для API (endpoint'ы
  // subscriptions/cancel/downsell ждут identity-guid, а тут токен) и адрес
  // не чистим (тем же паттерном, что у неудачного резолва ?code= выше —
  // ничего чистить не нужно, ссылка и так мертва).
  useEffect(() => {
    if (!initialGuid) return;
    let alive = true;
    void exchangePortalGuidForSession(projectId, initialGuid).then((token) => {
      if (!alive) return;
      if (!token) {
        setGuidInvalid(true);
        setLoading(false);
        return;
      }
      scrubEntryParamsFromUrl();
      storePortalCredential({ projectId, sessionToken: token });
      setCredential({ kind: 'session', token });
    });
    return () => {
      alive = false;
    };
  }, [initialGuid, projectId]);

  // WEB-3413: номер — из адреса сразу, до первого кадра (эффект раскладки
  // идёт до отрисовки браузером), даже если его затмил живой вход.
  useLayoutEffect(() => {
    if (portalActive) scrubEntryNumberFromUrl();
  }, [portalActive]);

  // WEB-3413: по номеру — только маска почты покупки (сервер не отдаёт ни
  // почту целиком, ни guid). null → обычная форма.
  useEffect(() => {
    if (!entryNumber) return;
    let alive = true;
    void describePortalEntryNumber(projectId, entryNumber).then((masked) => {
      if (!alive) return;
      setEntryMaskedEmail(masked);
      setEntryPending(false);
    });
    return () => {
      alive = false;
    };
  }, [entryNumber, projectId]);

  const requestEntryByNumber = useCallback(
    async (language?: string): Promise<boolean> =>
      entryNumber
        ? requestPortalEntryByNumber(projectId, entryNumber, language)
        : false,
    [entryNumber, projectId],
  );
  const forgetEntryNumber = useCallback(() => {
    setEntryNumber(null);
    setEntryMaskedEmail(null);
    setEntryPending(false);
  }, []);

  useEffect(() => {
    if (!credential || !effectiveProjectId) {
      // Пока code-резолв ИЛИ guid-обмен (WEB-1416 п.1) в полёте, загрузку не
      // сбрасываем — иначе мигнёт «ссылка недействительна» до ответа.
      // WEB-3413: то же, пока в пути маска почты по номеру из письма.
      if (!code && !initialGuid && !entryPending) setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    void fetchPortalSubscriptions(
      effectiveProjectId,
      credentialToAccess(credential),
    ).then((subs) => {
      if (!alive) return;
      if (subs === 'denied') {
        // WEB-1192 (п.3): вход не признан. Для сессии это штатное протухание
        // (TTL ~24 ч) → чистим мёртвый токен и ведём к запросу новой ссылки;
        // для легаси-guid — прежнее поведение (экран ошибки загрузки).
        if (credential.kind === 'session') {
          clearStoredPortalCredential();
          setCredential(null);
          setSessionExpired(true);
        } else {
          setError(true);
        }
      } else if (subs === null) {
        setError(true);
      } else {
        setSubscriptions(subs);
      }
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [effectiveProjectId, credential, code, initialGuid, entryPending]);

  const cancel = useCallback(
    async (subscriptionId: string): Promise<PortalCancelResult | null> => {
      if (!credential) return null;
      const result = await requestPortalCancel(
        effectiveProjectId,
        credentialToAccess(credential),
        subscriptionId,
      );
      // FIX-P12-1 (находка 3): 404 = сессия портала не признана — тот же
      // путь, что у списка подписок (:605-611 выше): для сессионного
      // доступа — протухание (чистим хранилище, ведём к новому входу); для
      // легаси-guid — прежний общий экран ошибки. Возвращаем null наружу —
      // внешний контракт cancel() не меняется, кнопка увидит «не удалось»
      // на долю кадра до перерисовки на hasAccess=false.
      if (result === 'denied') {
        if (credential.kind === 'session') {
          clearStoredPortalCredential();
          setCredential(null);
          setSessionExpired(true);
        } else {
          setError(true);
        }
        return null;
      }
      if (result) {
        // Локальное зеркало: бэкенд идемпотентен, все три статуса результата
        // означают «отмена запланирована» — подписку помечаем сразу.
        setSubscriptions((prev) =>
          prev.map((s) =>
            s.subscriptionId === subscriptionId
              ? {
                  ...s,
                  cancelAtPeriodEnd: true,
                  currentPeriodEnd:
                    result.currentPeriodEnd ?? s.currentPeriodEnd,
                }
              : s,
          ),
        );
      }
      return result;
    },
    [effectiveProjectId, credential],
  );

  // WEB-1395: возобновление — зеркально cancel() выше: тот же путь протухшей
  // сессии ('denied'), после успеха локальное зеркало снимает флаг отмены —
  // строки перерисуются (resume-кнопка уйдёт, кнопка отмены вернётся).
  const resume = useCallback(
    async (subscriptionId: string): Promise<PortalResumeOutcome> => {
      if (!credential) return { ok: false, reason: 'unavailable' };
      const outcome = await requestPortalResume(
        effectiveProjectId,
        credentialToAccess(credential),
        subscriptionId,
      );
      if (!outcome.ok && outcome.reason === 'denied') {
        if (credential.kind === 'session') {
          clearStoredPortalCredential();
          setCredential(null);
          setSessionExpired(true);
        } else {
          setError(true);
        }
        return outcome;
      }
      if (outcome.ok) {
        setSubscriptions((prev) =>
          prev.map((s) =>
            s.subscriptionId === subscriptionId
              ? {
                  ...s,
                  cancelAtPeriodEnd: false,
                  currentPeriodEnd:
                    outcome.result.currentPeriodEnd ?? s.currentPeriodEnd,
                }
              : s,
          ),
        );
      }
      return outcome;
    },
    [effectiveProjectId, credential],
  );

  const downsell = useCallback(
    async (
      subscriptionId: string,
      targetPriceId: string,
    ): Promise<PortalDownsellOutcome> => {
      // Цена меняется со СЛЕДУЮЩЕГО периода (proration none) — текущее
      // зеркало подписки не трогаем, блок сам показывает success-состояние.
      if (!credential) return { ok: false, reason: 'unavailable' };
      const outcome = await requestPortalDownsell(
        effectiveProjectId,
        credentialToAccess(credential),
        subscriptionId,
        targetPriceId,
      );
      // FIX-P12-1 (находка 3): симметрично cancel() выше — 'denied' = сессия
      // портала не признана, тот же путь, что и у списка подписок.
      if (!outcome.ok && outcome.reason === 'denied') {
        if (credential.kind === 'session') {
          clearStoredPortalCredential();
          setCredential(null);
          setSessionExpired(true);
        } else {
          setError(true);
        }
      }
      return outcome;
    },
    [effectiveProjectId, credential],
  );

  const value = useMemo<PortalActionsValue>(
    () => ({
      projectId: effectiveProjectId,
      hasAccess: credential !== null,
      sessionExpired,
      codeInvalid,
      guidInvalid,
      loading,
      error,
      subscriptions,
      claimInviteSlot,
      releaseInviteSlot,
      entryEmail,
      setEntryEmail,
      entryLinkSent,
      markEntryLinkSent,
      entryMaskedEmail,
      requestEntryByNumber,
      forgetEntryNumber,
      cancel,
      resume,
      downsell,
      downsellOffer,
      registerDownsellOffer,
      unregisterDownsellOffer,
    }),
    [
      effectiveProjectId,
      credential,
      sessionExpired,
      codeInvalid,
      guidInvalid,
      loading,
      error,
      subscriptions,
      claimInviteSlot,
      releaseInviteSlot,
      entryEmail,
      entryLinkSent,
      markEntryLinkSent,
      entryMaskedEmail,
      requestEntryByNumber,
      forgetEntryNumber,
      cancel,
      resume,
      downsell,
      downsellOffer,
      registerDownsellOffer,
      unregisterDownsellOffer,
      // Версия реестра сама по себе в значение не входит, но обязана менять
      // его идентичность: иначе оставшиеся блоки не перерисуются, когда
      // хозяин приглашения ушёл с экрана.
      inviteRegistryVersion,
      // Аналогично — конфиг downsell_offer читается из ref (downsellOffer
      // выше), версия только триггерит пересчёт value на изменение реестра.
      downsellRegistryVersion,
    ],
  );

  return (
    <PortalActionsContext.Provider value={value}>
      {children}
    </PortalActionsContext.Provider>
  );
}

/**
 * ISO-дата → человеческая строка конца периода; null-safe.
 *
 * WEB-1905: язык — ЯЗЫК ВОРОНКИ, а не браузера и не «всегда английский».
 * Прежний `toLocaleDateString(undefined, …)` брал язык браузера, и у
 * покупателя с русским браузером «1 сентября 2026 г.» стояло посреди
 * английских надписей; фикс F1 (ревизия пачки 15) прибил дату к `en` — это
 * снимало смесь, но врало испанской воронке. Теперь тег даёт тот же словарь,
 * что и надписи (`resolvePublicQuizLocale` внутри `formatPublicQuizDate`),
 * поэтому текст и дата не могут разъехаться языками.
 */
export function formatPortalDate(
  iso: string | null,
  language?: string | null,
): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return formatPublicQuizDate(
    date,
    { year: 'numeric', month: 'long', day: 'numeric' },
    language,
  );
}
